import { Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { NumberSeparatorUtilitiesService } from '@utilities/number-separator-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TEventKpiTrend, TEventKpiTrendTone, TEventKpiValueType } from './event-kpi-card.interface';

@Component({
  imports: [
    TranslateModule,
    MatButtonModule,
    MatTooltipModule,
    IconComponent,
    NgxSkeletonLoaderModule,
    TextClampTooltipDirective,
  ],
  selector: 'app-event-kpi-card',
  templateUrl: './event-kpi-card.component.html',
})
export class EventKpiCardComponent {
  private readonly numberSeparatorUtilities = inject(NumberSeparatorUtilitiesService);
  private readonly durationUtilities = inject(DurationUtilitiesService);

  public readonly title = input.required<string>();
  public readonly infoMessage = input<string | null>(null);
  public readonly valueType = input<TEventKpiValueType>('integer');
  public readonly currentValue = input<number | null>(null);
  public readonly totalValue = input<number | null>(null);
  public readonly comparedCurrentValue = input<number | null>(null);
  public readonly enableProgressBar = input<boolean>(false);
  /** Flips the delta badge's colors - a rise shows red and a drop green - for KPIs where a smaller value is better. */
  public readonly lowerIsBetter = input<boolean>(false);
  /** Whether compare mode is active - shows a loading placeholder for the delta badge while `comparedCurrentValue` hasn't arrived yet, instead of hiding it outright (which would look identical to "no comparison selected"). */
  public readonly comparing = input<boolean>(false);
  public readonly loading = input<boolean>(false);

  public readonly formattedValue = computed<string>(() => this.formatValue(this.currentValue()));

  public readonly formattedTotalValue = computed<string | null>(() => {
    const total = this.totalValue();

    return total === null || total === undefined ? null : this.formatValue(total, 'integer');
  });

  /** Fill percentage for the progress bar (0-100), capped at 100% regardless of the source ratio. */
  public readonly progressPercentage = computed<number>(() => {
    const current = this.currentValue();

    if (!this.enableProgressBar() || current === null || current === undefined) {
      return 0;
    }

    const total = this.totalValue();
    const ratio = this.valueType() === 'percentage' ? current : total ? current / total : current / 100;

    return Math.max(0, Math.min(100, ratio * 100));
  });

  public readonly percentageChange = computed<number | null>(() => {
    const current = this.currentValue();
    const compared = this.comparedCurrentValue();

    if (current === null || current === undefined || compared === null || compared === undefined) {
      return null;
    }

    // The comparison period's value is the correct denominator (e.g. 100 -> 120 is +20%, not
    // +16.7%) - guard on it being 0 rather than on `current`, since that's the actual
    // division-by-zero risk once the denominator is fixed. Going from 0 shows -100% (a full
    // drop); going from a 0 baseline to anything above 0 is treated as a full +100% increase,
    // since a true percentage is undefined in that case.
    if (compared === 0) {
      return current === 0 ? 0 : 100;
    }

    return ((current - compared) / compared) * 100;
  });

  public readonly trend = computed<TEventKpiTrend>(() => {
    const change = this.percentageChange();

    if (!change) {
      return 'neutral';
    }

    return change > 0 ? 'up' : 'down';
  });

  public readonly trendTone = computed<TEventKpiTrendTone>(() => {
    const trend = this.trend();

    if (trend === 'neutral') {
      return 'neutral';
    }

    return (trend === 'up') !== this.lowerIsBetter() ? 'positive' : 'negative';
  });

  public readonly formattedPercentageChange = computed<string | null>(() => {
    const change = this.percentageChange();

    if (change === null) {
      return null;
    }

    return `${this.numberSeparatorUtilities.formatNumberToLocaleString(Math.abs(change), null, 1, 1, true)}%`;
  });

  private formatValue(value: number | null, overrideType?: TEventKpiValueType): string {
    if (value === null || value === undefined) {
      return '-';
    }

    switch (overrideType ?? this.valueType()) {
      case 'percentage':
        return `${this.numberSeparatorUtilities.formatNumberToLocaleString(value * 100, null, 1, 1, true)}%`;
      case 'float':
        // minimumFractionDigits: 1, maximumFractionDigits: 2 - `toLocaleString` rounds to 2
        // decimals but trims a trailing zero below the minimum, so 0.20 -> "0.2" while 0.01 and
        // 1.32 keep both decimals since they're both significant.
        return this.numberSeparatorUtilities.formatNumberToLocaleString(value, null, 1, 2, true);
      case 'currency':
        // Always exactly 2 decimals (unlike `float`) - money shouldn't drop a trailing zero.
        return `$${this.numberSeparatorUtilities.formatNumberToLocaleString(value, null, 2, 2, true)}`;
      case 'duration':
        return this.durationUtilities.getDuration(value, 'milliseconds');
      case 'milliseconds':
        return `${this.numberSeparatorUtilities.formatNumberToLocaleString(Math.round(value), null, 0, 0, true)} ms`;
      case 'integer':
      default:
        return this.numberSeparatorUtilities.formatNumberToLocaleString(Math.round(value), null, 0, 0, true);
    }
  }
}
